
import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Article {
  id: string;
  title: string;
  content_body: string | null;
  featured_image_url: string | null;
  published_at: string;
  content_type: string;
}

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const NewsArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (slug) {
      fetchArticle(slug);
    } else {
      setNotFound(true);
      setLoading(false);
    }
  }, [slug]);

  const fetchArticle = async (articleSlug: string) => {
    try {
      const { data, error } = await supabase
        .from('cms_posts')
        .select('id, title, content_body, featured_image_url, published_at, content_type')
        .eq('slug', articleSlug)
        .eq('status', 'published')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          setNotFound(true);
        } else {
          throw error;
        }
        return;
      }

      if (data) {
        setArticle({
          ...data,
          id: String(data.id)
        });
      } else {
        setNotFound(true);
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to load article',
        description: 'Please try refreshing the page',
      });
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-warm-50 py-12 px-4 flex items-center justify-center pt-16">
          <div className="flex items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            <span className="text-gray-700 font-medium">Loading article...</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-warm-50 py-12 px-4 pt-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h1 className="text-3xl font-serif font-medium text-gray-900 mb-4">Article Not Found</h1>
              <p className="mb-6 text-gray-600">The article you're looking for doesn't seem to exist.</p>
              <Button asChild className="bg-brand-500 hover:bg-brand-600">
                <Link to="/news-events">Back to News & Events</Link>
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-warm-50 py-8 px-4 pt-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button variant="outline" className="mb-6 border-warm-200 hover:bg-warm-50" asChild>
            <Link to="/news-events">
              <ArrowLeft size={16} className="mr-2" /> Back to News & Events
            </Link>
          </Button>
          
          {/* Article Container */}
          <article className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Featured Image */}
            {article.featured_image_url && (
              <div className="w-full h-64 md:h-80 lg:h-96 overflow-hidden">
                <img 
                  src={article.featured_image_url} 
                  alt={article.title} 
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            {/* Article Header */}
            <header className="p-6 md:p-8 border-b border-gray-100">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-4 leading-tight">
                {article.title}
              </h1>
              
              <div className="flex items-center text-gray-600">
                <Calendar className="h-4 w-4 mr-2 text-brand-600" />
                <time className="text-sm font-medium">
                  {formatDate(article.published_at)}
                </time>
              </div>
            </header>
            
            {/* Article Content */}
            <div className="p-6 md:p-8">
              {article.content_body ? (
                <div className="prose prose-lg max-w-none">
                  {article.content_body.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 text-lg">No content available for this article.</p>
                </div>
              )}
            </div>
          </article>
          
          {/* Bottom Navigation */}
          <div className="mt-8 text-center">
            <Button asChild className="bg-brand-500 hover:bg-brand-600">
              <Link to="/news-events">View More Articles</Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NewsArticle;
